import { CircuitResponse } from '@/types/circuits/CircuitResponse';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router';
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useDeleteCircuit,
  useDuplicateCircuit,
  useUpdateCircuit,
} from '@/hooks/useCircuits';
import StyledContainer from '../styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '../styled/StyledFormHeaderWithBreadcrumb';
import StyledButton from '../styled/StyledButton';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import CopyIcon from '@/assets/icons/copy-03.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import HorizontalLine from '../ui/HorizontalLine';
import StyledButtonLoader from '../styled/StyledButtonLoader';
import StyledStepper from '../styled/StyledStepper';
import { Label } from '../ui/label';
import { ArrowRightIcon } from 'lucide-react';
import CircuitDaysList from './CircuitDaysList';
import { PATHS } from '@/constants/Paths';
import { buildCircuitsListSearch } from '@/utils/searchParams/buildCircuitsListSearch';
import { getListSearchFromLocation } from '@/utils/searchParams/getListSearchFromLocation';
import { parseCircuitDaysToSubmit } from '@/utils/circuits/parseCircuitDaysToSubmit';
import { getFileKeyFromUrl } from '@/utils/s3/getFileKeyFromUrl';
import { loadCircuitDefaultValues } from '@/utils/circuits/loadCircuitDefaultValues';
import { formatCircuitCode } from '@/utils/displayCodes';
import ModalDeletion from '../ui/ModalDeletion';
import CircuitSummary from './CircuitSummary';
import clsx from 'clsx';
import ModalDuplication from '../ui/ModalDuplication';
import { toastOptions, useToast } from '@/hooks/useToast';
import { toast } from 'react-toastify';
import { CIRCUIT_DUPLICATION_FIELDS } from '@/constants/circuitDuplicationFields';
import CircuitConfiguration from './CircuitConfiguration';
import CircuitFormDetailsView from './CircuitFormDetailsView';
import { CIRCUIT_STEPS } from '@/constants/steppers/circuitSteps';
import { useCircuitPrices } from '@/hooks/useCircuitPrices';
import { getCircuitTotalPrice } from '@/utils/circuits/prices/getCircuitTotalPrice';
import { startOfTomorrow } from 'date-fns';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { SupportedLocale } from '@/types/supported-locale.type';
import {
  CircuitFormDataWithTranslations,
  CircuitSchemaWithTranslations,
} from '@/i18n/translation-type-utils';
import { useTranslateFields } from '@/hooks/useTranslate';
import LanguageSwitchWithTranslateBtn from '../translation/LanguageSwitchWithTranslateBtn';
import { TRANSLATABLE_FIELDS } from '@/i18n/translatable-fields';
import { appendCircuitParticipantCountsToFormData } from '@/utils/circuits/participantCounts';
import Modal from '../ui/Modal';
import { ParagraphOverrides } from '@/api/circuits.api';
import { collectEditedParagraphOverrideKeys } from '@/hooks/useParagraphOverrideHighlight';
import { SelectedEstimateOption } from '@/types/estimates/SelectedEstimateOptionType';
import { CircuitBookingPreviewState } from '@/types/circuits/CircuitBookingPreview';
import {
  DeleteCircuitErrorCode,
  parseDeleteCircuitErrorCode,
} from '@/utils/circuits/deleteCircuitError';
import { saveCircuitErrorToast } from '@/utils/circuits/saveCircuitError';

interface ViewCircuitFormProps {
  circuit: CircuitResponse;
  language: SupportedLocale;
  setLanguage: React.Dispatch<React.SetStateAction<SupportedLocale>>;
  paragraphOverrides?: ParagraphOverrides;
}

type SubmitFormType = (
  isDraft?: boolean,
) => SubmitHandler<CircuitFormDataWithTranslations>;

const ViewCircuitForm = ({
  circuit,
  language,
  setLanguage,
  paragraphOverrides,
}: ViewCircuitFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const circuitDefaultValues = useMemo(
    () => loadCircuitDefaultValues({ circuit, t }),
    [circuit, t],
  );

  const methods = useForm<CircuitFormDataWithTranslations>({
    mode: 'onChange',
    resolver: zodResolver(CircuitSchemaWithTranslations),
    defaultValues: circuitDefaultValues,
  });

  const {
    control,
    setValue,
    getValues,
    reset,
    handleSubmit,
    formState: { isValid, errors, defaultValues },
  } = methods;

  const currentName = useWatch({ control, name: 'name' });
  const selectedCountries = useWatch({ control, name: 'countries' });
  const numberOfDays = useWatch({ control, name: 'nbDays' });
  const numberOfParticipants = useWatch({ control, name: 'nbParticipants' });

  useEffect(() => {
    if (numberOfDays && numberOfDays >= 1) {
      setValue('nbNights', numberOfDays - 1);
    }
  }, [numberOfDays, setValue]);

  const [currStep, setCurrStep] = useState(0);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [isDuplicationModalOpen, setIsDuplicationModalOpen] = useState(false);
  const [deleteErrorCode, setDeleteErrorCode] =
    useState<DeleteCircuitErrorCode | null>(null);

  const { mutateAsync: updateCircuit } = useUpdateCircuit(language);
  const { mutateAsync: deleteCircuit } = useDeleteCircuit();
  const { mutateAsync: duplicateCircuit } = useDuplicateCircuit();

  const isSaveDisabled = useMemo(
    () =>
      !currentName ||
      selectedCountries[0].country == undefined ||
      !numberOfDays ||
      !numberOfParticipants,
    [currentName, numberOfDays, numberOfParticipants, selectedCountries],
  );

  const [circuitDayIndex, setCircuitDayIndex] = useState(0);
  const [suppressedOverrideFields, setSuppressedOverrideFields] = useState<
    Set<string>
  >(new Set());
  const circuitUpdatedAtRef = useRef(circuit.updatedAt);

  const errorsLength = useMemo(() => Object.keys(errors).length, [errors]);

  useEffect(() => {
    if (circuit.updatedAt !== circuitUpdatedAtRef.current) {
      circuitUpdatedAtRef.current = circuit.updatedAt;
      reset(circuitDefaultValues);
    }
  }, [circuit.updatedAt, circuitDefaultValues, reset]);

  useEffect(() => {
    if (!paragraphOverrides) return;

    setSuppressedOverrideFields((previous) => {
      const next = new Set(previous);
      let changed = false;

      for (const key of previous) {
        if (!paragraphOverrides[key]?.length) {
          next.delete(key);
          changed = true;
        }
      }

      return changed ? next : previous;
    });
  }, [paragraphOverrides]);

  const { translateFields, isTranslating } = useTranslateFields({
    targetLanguage: language,
    control,
    setValue,
    fields: TRANSLATABLE_FIELDS.Circuit,
    nestedFields: {
      circuitDays: ['stepTitle', 'description'],
    },
  });

  const circuitDayErrorIndex = useMemo(() => {
    if (errors.circuitDays) {
      return +Object.keys(errors.circuitDays)[0];
    }
    return null;
  }, [errors.circuitDays]);

  const hasNavigatedToErrorStepRef = useRef(false);
  const prevHasErrorsRef = useRef(false);

  useEffect(() => {
    const hasErrors = !!errors.circuitDays;

    if (
      hasErrors &&
      !prevHasErrorsRef.current &&
      !hasNavigatedToErrorStepRef.current
    ) {
      startTransition(() => {
        setCurrStep(1);
      });
      hasNavigatedToErrorStepRef.current = true;
    }

    // Reset navigation flag when errors clear
    if (!hasErrors && prevHasErrorsRef.current) {
      hasNavigatedToErrorStepRef.current = false;
    }

    prevHasErrorsRef.current = hasErrors;
  }, [errors.circuitDays]);

  const { toastAsync } = useToast();

  const submitForm: SubmitFormType = useCallback(
    (isDraft) => {
      return async (data) => {
        const formData = new FormData();
        const addIf = (value: string | Blob | undefined, key: string) => {
          if (value !== undefined) formData.append(key, value);
        };
        if (!isDraft) formData.append('status', SupplierStatus.PUBLISHED);
        addIf(data.name, 'name');
        addIf(data.remarks, 'remarks');
        data.countries.forEach(({ country }) => {
          addIf(country?.value, 'countries[]');
        });
        data.countries.forEach(({ currency }) => {
          addIf(currency?.value, 'currencies[]');
        });
        // Always send marginRate: an empty string clears it (backend -> null).
        // Without this, clearing the field sends nothing and Prisma keeps the
        // previous value, so the rate would appear "stuck" (e.g. 20%).
        formData.append(
          'marginRate',
          data.marginRate !== undefined ? String(data.marginRate) : '',
        );
        if (data.nbDays) formData.append('nbDays', String(data.nbDays));
        if (data.nbNights) formData.append('nbNights', String(data.nbNights));
        appendCircuitParticipantCountsToFormData(formData, {
          nbParticipants: data.nbParticipants,
          nbAdolescents: data.nbAdolescents,
          nbChildren: data.nbChildren,
        });
        if (data.childAges && data.childAges.length) {
          formData.append(
            'childAgeFrom',
            String(data.childAges[0].childAgeFrom),
          );
          formData.append('childAgeTo', String(data.childAges[0].childAgeTo));
          if (data.childAges.length > 1) {
            formData.append(
              'secondChildAgeFrom',
              String(data.childAges[1].childAgeFrom),
            );
            formData.append(
              'secondChildAgeTo',
              String(data.childAges[1].childAgeTo),
            );
          }
        }
        if (data.mainImage && data.mainImage instanceof Blob) {
          formData.append('files', data.mainImage);
          formData.append('hasMainImage', true.toString());
        } else {
          if (data.mainImage)
            formData.append(
              'mainImageUrl',
              getFileKeyFromUrl(data.mainImage as string),
            );
          formData.append('hasMainImage', false.toString());
        }
        if (data.images && data.images.length) {
          data.images
            .filter((image) => image instanceof Blob)
            .forEach((image) => formData.append('files', image));
          formData.append(
            'imagesCount',
            data.images
              .filter((image) => image instanceof Blob)
              .length.toString(),
          );
          data.images
            .filter((image) => !(image instanceof Blob))
            .forEach((image) =>
              formData.append(
                'imagesUrls[]',
                getFileKeyFromUrl(image as string),
              ),
            );
        }
        addIf(data.summary, 'summary');
        addIf(data.importantPoints, 'importantPoints');
        if (data.additionalParagraphs) {
          formData.append(
            'additionalParagraphs',
            JSON.stringify(data.additionalParagraphs),
          );
        }
        addIf(data.priceIncludes, 'priceIncludes');
        addIf(data.priceExcludes, 'priceExcludes');
        addIf(data.modificationsConditions, 'modificationConditions');
        addIf(data.cancellationConditions, 'cancellationConditions');
        if (data.circuitDays) {
          formData.append(
            'circuitDays',
            JSON.stringify(parseCircuitDaysToSubmit(data.circuitDays)),
          );
          data.circuitDays.forEach((circuitDay) => {
            circuitDay.images
              ?.filter((image) => image instanceof Blob)
              .forEach((image) => formData.append('files', image));
          });
        }
        formData.append('translations', JSON.stringify(data.translations));
        const editedOverrideKeys = collectEditedParagraphOverrideKeys({
          values: getValues(),
          baseline: defaultValues as CircuitFormDataWithTranslations,
          language,
          paragraphOverrides,
        });
        await toastAsync(
          updateCircuit({ circuitId: circuit.id, circuit: formData }),
          {
            success: 'Circuit mis à jour avec succès',
            error: saveCircuitErrorToast(t),
          },
        );
        if (editedOverrideKeys.length > 0) {
          setSuppressedOverrideFields(
            (previous) => new Set([...previous, ...editedOverrideKeys]),
          );
        }
        if (!isDraft) {
          const listSearch = getListSearchFromLocation(
            location,
            buildCircuitsListSearch,
          );
          navigate(PATHS.home.circuits.list + listSearch);
        }
      };
    },
    [
      circuit.id,
      defaultValues,
      getValues,
      language,
      navigate,
      paragraphOverrides,
      t,
      toastAsync,
      updateCircuit,
      location,
    ],
  );

  const runValidatedSubmit = useCallback(
    (isDraft: boolean) =>
      new Promise<void>((resolve, reject) => {
        void handleSubmit(
          async (data) => {
            try {
              await submitForm(isDraft)(data);
              resolve();
            } catch (error) {
              reject(error);
            }
          },
          () => {
            reject(new Error('validation_failed'));
          },
        )();
      }),
    [handleSubmit, submitForm],
  );

  const saveCircuit = useCallback(async () => {
    if (circuit.status === SupplierStatus.DRAFT) {
      setIsEditEnabled(false);
      await submitForm(true)(getValues());
      return;
    }
    await runValidatedSubmit(true);
  }, [circuit.status, getValues, runValidatedSubmit, submitForm]);

  const publishCircuit = useCallback(
    () => runValidatedSubmit(false),
    [runValidatedSubmit],
  );

  const circuitDays = useWatch({ control, name: 'circuitDays' });

  const { circuitDaysPriceByStartDate } = useCircuitPrices({
    days: circuitDays,
    circuitMarginRate: circuit.marginRate,
    nbParticipants: Number(numberOfParticipants),
    nbChildren: Number(useWatch({ control, name: 'nbChildren' })),
    nbAdolescents: Number(useWatch({ control, name: 'nbAdolescents' })),
    targetCurrency: circuit.currencies[0],
    enabled: currStep >= 2,
  });

  const dayWithMinimumPrice = useMemo(
    () =>
      currStep >= 2
        ? getCircuitTotalPrice(circuitDaysPriceByStartDate).date
        : null,
    [circuitDaysPriceByStartDate, currStep],
  );

  const [selectedArrivalDate, setSelectedArrivalDate] = useState(
    dayWithMinimumPrice || startOfTomorrow(),
  );

  const [bookingPreviewNbAdults, setBookingPreviewNbAdults] = useState(() =>
    Number(circuitDefaultValues.nbParticipants ?? 0),
  );
  const [bookingPreviewNbChildren, setBookingPreviewNbChildren] = useState(() =>
    Number(circuitDefaultValues.nbChildren ?? 0),
  );
  const [
    bookingPreviewChildrenBirthDates,
    setBookingPreviewChildrenBirthDates,
  ] = useState<Record<number, Date>>({});
  const [bookingPreviewSelectedOptions, setBookingPreviewSelectedOptions] =
    useState<Record<string, SelectedEstimateOption>>({});

  const bookingPreview = useMemo<CircuitBookingPreviewState>(
    () => ({
      nbAdults: bookingPreviewNbAdults,
      nbChildren: bookingPreviewNbChildren,
      nbAdolescents: Number(circuitDefaultValues.nbAdolescents ?? 0),
      childrenBirthDates: bookingPreviewChildrenBirthDates,
      selectedOptions: bookingPreviewSelectedOptions,
      setNbAdults: setBookingPreviewNbAdults,
      setNbChildren: setBookingPreviewNbChildren,
      setChildrenBirthDates: setBookingPreviewChildrenBirthDates,
      setSelectedOptions: setBookingPreviewSelectedOptions,
    }),
    [
      bookingPreviewChildrenBirthDates,
      bookingPreviewNbAdults,
      bookingPreviewNbChildren,
      bookingPreviewSelectedOptions,
      circuitDefaultValues.nbAdolescents,
    ],
  );

  const CurrentStep = useMemo(() => {
    switch (currStep) {
      case 0:
        return (
          <>
            {errorsLength > 0 && (
              <Label className="text-error-500">
                {t('circuits.viewForm.checkRequiredFields')}
              </Label>
            )}
            <CircuitConfiguration
              control={control}
              isEditEnabled={isEditEnabled}
              language={language}
              translateFields={(fields) =>
                translateFields({ fieldsToTranslate: fields })
              }
              isTranslating={isTranslating}
              paragraphOverrides={paragraphOverrides}
              suppressedOverrideFields={suppressedOverrideFields}
            />
            {isEditEnabled && (
              <div className="flex flex-row-reverse p-6 md:w-fit md:sticky md:left-full md:bottom-16">
                <StyledButtonLoader
                  title={t('circuits.viewForm.save')}
                  successTitle={t('circuits.viewForm.saveSuccess')}
                  errorTitle={t('circuits.viewForm.checkRequiredFields')}
                  onClick={saveCircuit}
                  disabled={
                    circuit.status === SupplierStatus.DRAFT
                      ? isSaveDisabled
                      : !isValid
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
            )}
            <div className="flex flex-row-reverse p-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('circuits.viewForm.seeCircuitSteps')}
                onClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
                disabled={isSaveDisabled}
                className="font-semibold gap-1 px-3 py-2"
                icon={<ArrowRightIcon className="size-5" />}
                iconPosition="right"
              />
            </div>
          </>
        );
      case 1:
        return (
          <div className="relative">
            <CircuitDaysList
              control={control}
              language={language}
              circuitDayErrorIndex={circuitDayErrorIndex || circuitDayIndex}
              circuitCode={circuit.code}
              isViewMode={!isEditEnabled}
              paragraphOverrides={paragraphOverrides}
              suppressedOverrideFields={suppressedOverrideFields}
            />
            {isEditEnabled && (
              <div className="flex flex-row-reverse p-6 md:w-fit md:sticky md:left-full md:bottom-16">
                <StyledButtonLoader
                  title={t('circuits.viewForm.save')}
                  successTitle={t('circuits.viewForm.saveSuccess')}
                  errorTitle={t('circuits.viewForm.checkRequiredFields')}
                  onClick={saveCircuit}
                  disabled={
                    circuit.status === SupplierStatus.DRAFT
                      ? isSaveDisabled
                      : !isValid
                  }
                  className="font-semibold gap-1 px-3 py-2"
                  icon={<SaveIcon className="w-5 h-5" />}
                />
              </div>
            )}
            <div className="flex flex-row-reverse p-6 md:w-fit md:sticky md:left-full md:bottom-0">
              <StyledButton
                title={t('circuits.viewForm.showGlobalPricing')}
                onClick={() => {
                  setCurrStep(2);
                  window.scrollTo(0, 0);
                }}
                disabled={isSaveDisabled}
                className="font-semibold gap-1 px-3 py-2"
                icon={<ArrowRightIcon className="w-5 h-5" />}
                iconPosition="right"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <>
            <CircuitSummary
              control={control}
              onCircuitDayClick={(day) => {
                setCircuitDayIndex(day);
                setCurrStep(1);
              }}
              selectedArrivalDate={selectedArrivalDate}
              setSelectedArrivalDate={setSelectedArrivalDate}
              language={language}
            />
            <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10">
              <StyledButton
                title={t('circuits.viewForm.seeDescription')}
                className="font-semibold gap-1 px-3 py-2"
                onClick={() => {
                  setCurrStep(3);
                  window.scrollTo(0, 0);
                }}
                icon={<ArrowRightIcon className="size-5" />}
                iconPosition="right"
              />
            </div>
          </>
        );
      case 3:
      case 4:
        return (
          <>
            <CircuitFormDetailsView
              control={control}
              circuitId={circuit.id}
              circuitCurrency={circuit.currencies[0]}
              currStep={currStep}
              setCurrStep={setCurrStep}
              selectedArrivalDate={selectedArrivalDate}
              setSelectedArrivalDate={setSelectedArrivalDate}
              publishCircuit={
                circuit.status === SupplierStatus.DRAFT
                  ? handleSubmit(submitForm(false))
                  : undefined
              }
              errorsLength={errorsLength}
              isValid={isValid}
              language={language}
              bookingPreview={bookingPreview}
            />
            {currStep === 3 && (
              <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10">
                <StyledButton
                  title={t('circuits.viewForm.seeOptions')}
                  className="font-semibold gap-1 px-3 py-2"
                  onClick={() => {
                    setCurrStep(4);
                    window.scrollTo(0, 0);
                  }}
                  disabled={!selectedArrivalDate}
                  icon={<ArrowRightIcon className="size-5" />}
                  iconPosition="right"
                />
              </div>
            )}
            {currStep === 4 && circuit.status === SupplierStatus.DRAFT && (
              <div className="flex flex-row-reverse p-6 md:sticky md:bottom-0 z-10">
                <StyledButtonLoader
                  title={t('circuits.viewForm.publishCircuit')}
                  className="font-semibold gap-1 px-3 py-2"
                  onClick={publishCircuit}
                  successTitle={t('circuits.viewForm.publishedSuccess')}
                  errorTitle={t('circuits.viewForm.checkRequiredFields')}
                  icon={
                    <PlusIcon
                      className={clsx('size-5', {
                        'fill-white': isValid,
                        'fill-gray-400': !isValid,
                      })}
                    />
                  }
                  iconPosition="right"
                />
              </div>
            )}
          </>
        );
      default:
        return null;
    }
  }, [
    currStep,
    t,
    control,
    isEditEnabled,
    language,
    translateFields,
    isTranslating,
    circuit.status,
    circuit.code,
    circuit.id,
    circuit.currencies,
    saveCircuit,
    publishCircuit,
    isSaveDisabled,
    isValid,
    circuitDayErrorIndex,
    circuitDayIndex,
    selectedArrivalDate,
    bookingPreview,
    paragraphOverrides,
    suppressedOverrideFields,
  ]);

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || circuit.name}
          code={formatCircuitCode(circuit.code)}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('circuits.viewForm.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() => {
                const listSearch = getListSearchFromLocation(
                  location,
                  buildCircuitsListSearch,
                );
                navigate(PATHS.home.circuits.list + listSearch);
              }}
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('circuits.viewForm.delete')}
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2 text-gray-800 border-gray-300"
            onClick={() => {
              if ((circuit._count?.sharedCircuits ?? 0) > 0) {
                setDeleteErrorCode('CIRCUIT_SHARED_DELETE_FORBIDDEN');
                return;
              }
              setIsDeletionModalOpen(true);
            }}
          />
          <StyledButton
            variant="outlined"
            title={t('circuits.viewForm.duplicate')}
            className="font-semibold gap-1 px-3 py-2"
            onClick={() => setIsDuplicationModalOpen(true)}
            icon={<CopyIcon className="w-5 h-5 stroke-brand-600" />}
          />
          {!isEditEnabled ? (
            <StyledButton
              title={t('circuits.viewForm.edit')}
              onClick={() => setIsEditEnabled(true)}
              className="font-semibold gap-1 px-3 py-2"
              icon={<EditIcon className="w-5 h-5 stroke-white" />}
            />
          ) : (
            <>
              <StyledButton
                title={t('circuits.viewForm.cancel')}
                onClick={() => {
                  setIsEditEnabled(false);
                  reset();
                }}
                className="font-semibold gap-1 px-3 py-2"
              />
              <StyledButtonLoader
                title={t('circuits.viewForm.save')}
                successTitle={t('circuits.viewForm.saveSuccess')}
                errorTitle={t('circuits.viewForm.checkRequiredFields')}
                onClick={saveCircuit}
                disabled={
                  circuit.status === SupplierStatus.DRAFT
                    ? isSaveDisabled
                    : !isValid
                }
                className="font-semibold gap-1 px-3 py-2"
                icon={<SaveIcon className="w-5 h-5" />}
              />
            </>
          )}
        </StyledFormHeaderWithBreadcrumb>
        <StyledStepper
          activeStep={currStep}
          steps={getTranslatedConst(CIRCUIT_STEPS)}
          setStep={(step: number) => setCurrStep(step)}
          disableNext={isSaveDisabled}
          disableConditions={{
            4: !selectedArrivalDate,
          }}
        />
        <HorizontalLine />
        <LanguageSwitchWithTranslateBtn
          translateFields={() =>
            translateFields({
              allNestedFields: true,
              translateToAllLocales: true,
            })
          }
          isTranslating={isTranslating}
          language={language}
          setLanguage={setLanguage}
          hideTranslateBtn={!isEditEnabled || currStep > 1}
        />
        {CurrentStep}

        {isDeletionModalOpen && (
          <ModalDeletion
            closeModal={() => {
              setIsDeletionModalOpen(false);
            }}
            actionOnClick={() => {
              deleteCircuit({ circuitId: circuit.id })
                .then(() => {
                  toast.success(t('trash.movedToTrash.circuit'), toastOptions);
                  const listSearch = getListSearchFromLocation(
                    location,
                    buildCircuitsListSearch,
                  );
                  navigate(PATHS.home.circuits.list + listSearch);
                })
                .catch((error) => {
                  const errorCode = parseDeleteCircuitErrorCode(error);
                  if (errorCode) {
                    setIsDeletionModalOpen(false);
                    setDeleteErrorCode(errorCode);
                  }
                });
            }}
            type="circuit"
          />
        )}
        {deleteErrorCode === 'CIRCUIT_SHARED_DELETE_FORBIDDEN' && (
          <Modal
            closeModal={() => setDeleteErrorCode(null)}
            title={t('circuits.deleteBlocked.shared.title')}
            message={t('circuits.deleteBlocked.shared.message')}
            actionTitle={t('common.close')}
            actionOnClick={() => setDeleteErrorCode(null)}
            hasAction={false}
            cancelBtnLabel={t('common.close')}
          />
        )}
        {deleteErrorCode === 'CIRCUIT_LINKED_TO_ESTIMATES_DELETE_FORBIDDEN' && (
          <Modal
            closeModal={() => setDeleteErrorCode(null)}
            title={t('circuits.deleteBlocked.estimates.title')}
            message={t('circuits.deleteBlocked.estimates.message')}
            actionTitle={t('common.close')}
            actionOnClick={() => setDeleteErrorCode(null)}
            hasAction={false}
            cancelBtnLabel={t('common.close')}
          />
        )}
        {isDuplicationModalOpen && (
          <ModalDuplication
            actionOnClick={async (selectedFields) => {
              if (circuit.id) {
                const duplicatedCircuit = await duplicateCircuit({
                  circuitId: circuit.id,
                  fields: selectedFields.flatMap((fields) => fields.split(';')),
                });
                navigate(
                  PATHS.home.circuits.view.replace(
                    ':circuitId',
                    duplicatedCircuit.id,
                  ),
                  { state: location.state },
                );
              }
            }}
            closeModal={() => {
              setIsDuplicationModalOpen(false);
            }}
            fields={getTranslatedConst(CIRCUIT_DUPLICATION_FIELDS)}
            fieldsTitle={t('circuits.viewForm.configuration')}
            otherFields={circuit.circuitDays.map(({ day }) => ({
              label: t('circuits.viewForm.jour_', { index: day }),
              value: `Jour ${day}`,
            }))}
            otherFieldsTitle={t('circuits.viewForm.days')}
            type="circuit"
            code={circuit.code}
          />
        )}
      </StyledContainer>
    </FormProvider>
  );
};

export default ViewCircuitForm;
